import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories, statusLabels } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');
  const [filteredComics, setFilteredComics] = useState<Comic[]>([]);

  const currentCategory = categories.find((c) => c.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    let result = slug
      ? comics.filter((c) => c.category === slug)
      : [...comics];

    if (sortBy === 'latest') {
      result.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    } else {
      result.sort((a, b) => b.views - a.views);
    }

    setFilteredComics(result);
  }, [slug, sortBy]);

  const categoryName = currentCategory?.name || '全部分类';
  const categoryIcon = currentCategory?.icon || 'ri-apps-line';

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category/rexue' },
              { label: categoryName },
            ]}
          />

          {/* Category Header */}
          <section className="w-full px-4 md:px-8 py-8 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className={`${categoryIcon} text-lg`}></i>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">
                  {categoryName}漫画
                </h1>
              </div>
              <p className="text-sm text-foreground-500 ml-[52px]">
                共 {filteredComics.length} 部漫画 &mdash; 最新漫画更新、连载漫画同步阅读
              </p>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="w-full px-4 md:px-8 py-4 bg-background-50 border-b border-background-200/70 sticky top-16 z-30">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                <Link
                  to="/category/rexue"
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    !slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  全部
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Sort & Grid */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-foreground-500">
                  共找到 <strong className="text-foreground-800">{filteredComics.length}</strong> 部漫画
                </p>
                <div className="flex items-center gap-1 bg-background-100 rounded-full p-0.5">
                  <button
                    onClick={() => setSortBy('latest')}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === 'latest'
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    最新更新
                  </button>
                  <button
                    onClick={() => setSortBy('popular')}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      sortBy === 'popular'
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    人气最高
                  </button>
                </div>
              </div>

              {filteredComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                  {filteredComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                    <i className="ri-emotion-sad-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类暂无漫画内容</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}